import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Sparkles, Lock, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function Billing() {
  const { t } = useTranslation();
  const { tenant, setTenant } = useAuthStore();
  const [sub, setSub] = useState(null);
  const [card, setCard] = useState("4242 4242 4242 4242");
  const [togglingExpo, setTogglingExpo] = useState(false);
  const [removeExpoOpen, setRemoveExpoOpen] = useState(false);
  const [subscribeExpoOpen, setSubscribeExpoOpen] = useState(false);
  const [expoCard, setExpoCard] = useState("4242 4242 4242 4242");

  const load = async () => {
    const { data } = await api.get("/billing/subscription");
    setSub(data);
  };
  useEffect(() => {
    load();
  }, []);

  const refreshTenant = async () => {
    const { data } = await api.get("/tenant");
    setTenant(data);
  };

  const setExpoAddon = async (next, cardNumber) => {
    setTogglingExpo(true);
    try {
      const { data } = await api.post("/billing/addons/expo", {
        enabled: next,
        ...(cardNumber ? { card_number: cardNumber } : {}),
      });
      if (next && cardNumber) {
        toast.success(
          status === "trial"
            ? "Kartın doğrulandı — EXPO modülü başlatıldı 🎉 (deneme süresince ücretsiz)"
            : "Ödeme alındı — EXPO modülü başlatıldı 🎉",
        );
        setSubscribeExpoOpen(false);
      } else if (next) {
        toast.success("Kaldırma iptal edildi — EXPO aboneliğin devam ediyor 🎉");
      } else if (data?.cancel_at) {
        toast.success(
          `EXPO aboneliği ${new Date(data.cancel_at).toLocaleDateString("tr-TR")} tarihinde sonlandırılacak`,
        );
      } else {
        toast.success("EXPO modülü hesabınızdan kaldırıldı");
      }
      await Promise.all([load(), refreshTenant()]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTogglingExpo(false);
    }
  };

  const expo = sub?.addons?.expo;
  const expoCancelAt = expo?.cancel_at || null;
  const expoEndPreview = expoCancelAt || expo?.period_end || null;
  const expoDaysLeft = expoEndPreview
    ? Math.max(0, Math.ceil((new Date(expoEndPreview).getTime() - Date.now()) / 86400000))
    : null;

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/billing/subscribe", { card_number: card });
      toast.success(t("billing.activated"));
      // reload tenant
      const { data } = await api.get("/tenant");
      setTenant(data);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const status = tenant?.subscription_status;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          {t("brand")}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("billing.title")}
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-zinc-200 bg-white p-6">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            {t("billing.current_plan")}
          </div>
          <div className="mt-2 font-display text-2xl font-semibold text-zinc-950">
            Serpix Team · ${sub?.total_usd ?? 19}/mo
          </div>
          {sub?.addons?.expo?.enabled && (
            <div className="mt-1 text-xs text-zinc-500">
              $19 taban + ${sub.addons.expo.price_usd} EXPO eklentisi
            </div>
          )}
          <div className="mt-4 space-y-2 text-sm text-zinc-600">
            <div>
              {t("billing.status")}:{" "}
              <span className="font-medium text-zinc-900">{status}</span>
            </div>
            {sub?.trial_ends_at && (
              <div>Trial ends: {new Date(sub.trial_ends_at).toLocaleString()}</div>
            )}
            <div>Unlimited users</div>
          </div>
          {status === "active" && (
            <div className="mt-6 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <CheckCircle2 className="h-4 w-4" />
              {t("billing.activated")}
            </div>
          )}
        </div>

        <form
          onSubmit={subscribe}
          className="rounded-lg border border-zinc-200 bg-white p-6"
        >
          <div className="mb-4 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-zinc-500" />
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
              Payment (Mock Stripe)
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              {t("billing.card_number")}
            </Label>
            <Input
              required
              value={card}
              onChange={(e) => setCard(e.target.value)}
              data-testid={TEST_IDS.billingCardInput}
              className="mt-1"
            />
          </div>
          <Button
            type="submit"
            data-testid={TEST_IDS.billingSubscribeBtn}
            className="mt-4 w-full bg-zinc-900 text-white hover:bg-zinc-800"
          >
            {t("billing.subscribe")}
          </Button>
          <p className="mt-3 text-xs text-zinc-500">
            This is a mock payment. No real charges are made.
          </p>
        </form>
      </div>

      {/* Eklentiler */}
      <div className="rounded-lg border border-zinc-200 bg-white p-6" data-testid="billing-addons">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          Eklentiler
        </div>
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 text-violet-600" />
              <div>
                <div className="font-medium text-zinc-900">
                  EXPO — Fuar &amp; Etkinlik Modülü
                  <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                    +${sub?.addons?.expo?.price_usd ?? 10}/ay
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-zinc-500">
                  Fuar takvimi, şablon bazlı proje görevleri ve otomatik termin hesaplama.
                  {status === "trial" && " Deneme süresince ücretsizdir."}
                </p>
                {expoCancelAt && (
                  <p
                    className="mt-1 text-xs font-semibold text-amber-700"
                    data-testid="billing-expo-cancel-note"
                  >
                    Sonlandırılacak: {new Date(expoCancelAt).toLocaleDateString("tr-TR")} ·
                    kalan {expoDaysLeft} gün — bu tarihe kadar kullanmaya devam edebilirsin.
                  </p>
                )}
                {!expo?.enabled && expo?.purge_at && (
                  <p
                    className="mt-1 text-xs font-semibold text-red-700"
                    data-testid="billing-expo-purge-note"
                  >
                    EXPO verilerin {new Date(expo.purge_at).toLocaleDateString("tr-TR")} tarihinde
                    kalıcı olarak silinecek — bu tarihten önce yeniden eklersen verilerin korunur.
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={() => {
                if (!sub) return;
                if (expoCancelAt) {
                  setExpoAddon(true);
                } else if (expo?.enabled) {
                  setRemoveExpoOpen(true);
                } else {
                  setSubscribeExpoOpen(true);
                }
              }}
              disabled={togglingExpo || !sub}
              variant={expo?.enabled && !expoCancelAt ? "outline" : "default"}
              data-testid="billing-expo-toggle"
              className={
                expoCancelAt
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : expo?.enabled
                    ? "text-red-700 hover:bg-red-50"
                    : "bg-violet-600 text-white hover:bg-violet-700"
              }
            >
              {expoCancelAt ? "Kaldırmayı Geri Al" : expo?.enabled ? "Kaldır" : "Satın Al"}
            </Button>
          </div>
          {sub?.addons?.cop?.enabled && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-zinc-200 bg-zinc-50/60 p-4">
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-5 w-5 text-zinc-400" />
              <div>
                <div className="font-medium text-zinc-700">COP31 — Özel Modül</div>
                <p className="mt-0.5 text-xs text-zinc-500">
                  Bu modül Serpix ekibi tarafından hesabınıza tanımlanır. İlgileniyorsanız
                  hello@serpix.co adresinden bize ulaşın.
                </p>
              </div>
            </div>
            <span
              data-testid="billing-cop-status"
              className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700"
            >
              Tanımlı
            </span>
          </div>
          )}
        </div>
      </div>

      <Dialog open={subscribeExpoOpen} onOpenChange={setSubscribeExpoOpen}>
        <DialogContent data-testid="expo-subscribe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              EXPO Modülünü Başlat
            </DialogTitle>
            <DialogDescription>
              Fuar takvimi, fuar to-do listleri, şablon bazlı proje görevleri ve
              otomatik termin hesaplama.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-zinc-600">EXPO — Fuar &amp; Etkinlik Modülü</span>
                <span className="font-semibold text-zinc-900">
                  +${expo?.price_usd ?? 10}/ay
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between border-t border-zinc-200 pt-2">
                <span className="text-zinc-600">Yeni aylık toplam</span>
                <span
                  className="font-display text-lg font-semibold text-zinc-950"
                  data-testid="expo-subscribe-total"
                >
                  ${(sub?.amount_usd ?? 19) + (expo?.price_usd ?? 10)}/ay
                </span>
              </div>
              {status === "trial" && (
                <p className="mt-2 text-xs font-medium text-emerald-700">
                  Deneme süresince ücretsiz — kartın doğrulanır, tahsilat deneme
                  bitiminde başlar.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Kart Numarası
              </Label>
              <Input
                value={expoCard}
                onChange={(e) => setExpoCard(e.target.value)}
                placeholder="4242 4242 4242 4242"
                data-testid="expo-subscribe-card-input"
                className="mt-1"
              />
              <p className="mt-1.5 text-[11px] text-zinc-400">
                Mock ödeme — gerçek tahsilat yapılmaz. Test kartı: 4242 4242 4242 4242
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSubscribeExpoOpen(false)}
              data-testid="expo-subscribe-cancel-btn"
            >
              Vazgeç
            </Button>
            <Button
              type="button"
              disabled={togglingExpo || expoCard.replace(/\s/g, "").length < 12}
              onClick={() => setExpoAddon(true, expoCard)}
              data-testid="expo-subscribe-pay-btn"
              className="gap-2 bg-violet-600 text-white hover:bg-violet-700"
            >
              <CreditCard className="h-4 w-4" />
              {status === "trial"
                ? "Kartı Doğrula ve Başlat"
                : `Öde ve Başlat ($${expo?.price_usd ?? 10}/ay)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={removeExpoOpen} onOpenChange={setRemoveExpoOpen}>
        <AlertDialogContent data-testid="expo-remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              EXPO modülünü kaldırmak üzeresin
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-zinc-600">
                <p className="rounded-md border border-red-200 bg-red-50 p-3 font-medium text-red-800">
                  Bu modülü kaldırırsan, abonelik bitiminden 30 gün sonra EXPO
                  modülüne bağlı tüm veriler (fuarlar, fuar projeleri, görevler,
                  şablonlar, dosyalar) kalıcı şekilde silinecektir ve geri
                  alamazsın. Emin misin?
                </p>
                <p>
                  Ödediğin ücret abonelik süresinin sonuna kadar geçerlidir; o
                  tarihten itibaren yenilenmeyecektir. Ancak modülü o tarihe
                  kadar kullanmaya devam edebilirsin.
                </p>
                <p
                  className="rounded-md border border-zinc-200 bg-zinc-50 p-3"
                  data-testid="expo-remove-end-info"
                >
                  EXPO aboneliğinin bitiş tarihi:{" "}
                  <b>
                    {expoEndPreview
                      ? new Date(expoEndPreview).toLocaleDateString("tr-TR")
                      : "—"}
                  </b>
                  {expoDaysLeft != null && (
                    <>
                      {" "}· kalan <b>{expoDaysLeft} gün</b>
                    </>
                  )}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expo-remove-no-btn">
              HAYIR
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="expo-remove-yes-btn"
              onClick={() => setExpoAddon(false)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              EVET, KALDIR
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
